import { Page, Dialog } from 'web-mojo';
import OperatorProgressPanel from '@portal/routes/views/OperatorProgressPanel.js';
import LockActivityFeedPanel from '@portal/routes/views/LockActivityFeedPanel.js';
import ScheduleRouteDrawer from '@portal/routes/views/ScheduleRouteDrawer.js';
import StopRequestReviewDrawer from '@portal/routes/views/StopRequestReviewDrawer.js';

export default class OperationsBoardPage extends Page {
    constructor(options = {}) {
        super({
            title: 'Live View',
            icon: 'bi-broadcast',
            pageName: 'operations_board',
            route: 'group/live',
            permissions: ['routes', 'view_routes', 'manage_routes', 'manage_group'],
            requiresGroup: true,
            ...options,
        });

        this.routes = [];
        this.pendingRequests = [];
        this._pollTimer = null;
        this._wsHandler = this._onRouteEvent.bind(this);
    }

    async onInit() {
        app.events.on('route:stop_update', this._wsHandler);
        app.events.on('route:route_started', this._wsHandler);
        app.events.on('route:route_completed', this._wsHandler);
        this._startPolling();
    }

    async onEnter() {
        await super.onEnter();
        await Promise.all([this.loadRoutes(), this.loadRequests()]);
        this.render();
    }

    onDestroy() {
        app.events.off('route:stop_update', this._wsHandler);
        app.events.off('route:route_started', this._wsHandler);
        app.events.off('route:route_completed', this._wsHandler);
        this._stopPolling();
    }

    _startPolling() {
        this._pollTimer = setInterval(() => {
            this.loadRoutes();
            this.loadRequests();
        }, 30000);
    }

    _stopPolling() {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    async _onRouteEvent(data) {
        await this.loadRoutes();
        this.render();
    }

    async loadRequests() {
        const response = await app.api.get('/api/routes/stop_request', {
            group: app.groupId(),
            status: 'pending',
            size: 50,
        });
        if (response.success && response.data.status) {
            this.pendingRequests = response.data.data || [];
        } else {
            this.pendingRequests = [];
        }
    }

    async loadRoutes() {
        const today = new Date().toISOString().slice(0, 10);
        const response = await app.api.get('/api/routes/route', {
            group: app.groupId(),
            scheduled_date: today,
            size: 100,
        });

        if (!response.success || !response.data.status) {
            return;
        }

        this.routes = (response.data.data || []).filter(r =>
            ['scheduled', 'in_progress', 'completed'].includes(r.status)
        );
    }

    async onActionRefresh() {
        await Promise.all([this.loadRoutes(), this.loadRequests()]);
        this.render();
    }

    onActionScheduleRoute() {
        const drawer = new ScheduleRouteDrawer({});
        const dialog = Dialog.showDialog({
            title: 'Schedule Route',
            body: drawer,
            size: 'md',
            footer: false,
        });
        drawer.on('scheduled', async () => {
            dialog.hide();
            app.toast.success('Route scheduled');
            await this.loadRoutes();
            this.render();
        });
    }

    onActionReviewRequest(event, el) {
        const requestId = parseInt(el.getAttribute('data-request-id'));
        const request = this.pendingRequests.find(r => r.id === requestId);
        if (!request) return;

        const operatorName = request.operator?.display_name
            || request.operator?.user?.username
            || `Operator ${request.operator?.id || ''}`;

        const drawer = new StopRequestReviewDrawer({ request });
        const dialog = Dialog.showDialog({
            title: `Field Request — ${operatorName}`,
            body: drawer,
            size: 'md',
            footer: false,
        });
        drawer.on('approved', async () => {
            dialog.hide();
            await Promise.all([this.loadRoutes(), this.loadRequests()]);
            this.render();
        });
        drawer.on('rejected', async () => {
            dialog.hide();
            await this.loadRequests();
            this.render();
        });
    }

    async onActionQuickReject(event, el) {
        const requestId = parseInt(el.getAttribute('data-request-id'));
        const request = this.pendingRequests.find(r => r.id === requestId);
        if (!request) return;

        const operatorName = request.operator?.display_name
            || request.operator?.user?.username
            || `Operator ${request.operator?.id || ''}`;

        const confirmed = await Dialog.confirm({
            title: 'Reject Request',
            message: `Reject the field stop request from ${operatorName}?`,
            confirmText: 'Reject',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await app.api.patch(`/api/routes/stop_request/${requestId}`, {
            status: 'rejected',
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to reject request');
            return;
        }
        app.toast.success('Request rejected');
        await this.loadRequests();
        this.render();
    }

    onActionOpenLockFeed() {
        const panel = new LockActivityFeedPanel();
        Dialog.showDialog({
            title: 'Lock Activity Feed',
            body: panel,
            size: 'lg',
            footer: false,
        });
    }

    onActionViewMap() {
        app.sidebar.setActiveMenu('group_routes');
        app.navigate('?page=group/live/map');
    }

    onActionOpenRoute(event, el) {
        const routeId = el.getAttribute('data-route-id');
        const route = this.routes.find(r => String(r.id) === routeId);
        if (!route) return;

        const panel = new OperatorProgressPanel({ routeId });
        const operatorName = route.operator?.display_name || route.operator?.user?.username || 'Route';
        Dialog.showDialog({
            title: `${route.template?.name || 'Route'} — ${operatorName}`,
            body: panel,
            size: 'lg',
            footer: false,
        });
    }

    _requestCardHtml(req) {
        const operatorName = req.operator?.display_name
            || req.operator?.user?.username
            || `Operator ${req.operator?.id || ''}`;
        const label = req.label || 'Unlabelled request';
        const address = req.address || '';
        const time = req.created || req.created_at
            ? new Date(req.created || req.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
            : '';

        return `
            <div class="d-flex align-items-start justify-content-between gap-3 px-3 py-2 border-bottom">
                <div class="flex-grow-1 min-width-0">
                    <div class="small fw-semibold text-truncate">${label}</div>
                    <div class="text-muted small">
                        <i class="bi bi-person-fill me-1"></i>${operatorName}
                        ${address ? `<span class="ms-2"><i class="bi bi-geo-alt me-1"></i>${address}</span>` : ''}
                        ${time ? `<span class="ms-2 text-secondary">${time}</span>` : ''}
                    </div>
                </div>
                <div class="d-flex gap-1 flex-shrink-0">
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                        data-action="quick-reject" data-request-id="${req.id}"
                        title="Reject">
                        <i class="bi bi-x-lg"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-primary"
                        data-action="review-request" data-request-id="${req.id}">
                        Review
                    </button>
                </div>
            </div>
        `;
    }

    _routeCardHtml(route) {
        const templateName = route.template?.name || `Route ${route.id}`;
        const operatorName = route.operator?.display_name || route.operator?.user?.username;
        const hasOperator = !!route.operator;

        const statusConfig = {
            scheduled: { badge: 'bg-secondary', icon: 'bi-clock', label: 'Scheduled' },
            in_progress: { badge: 'bg-primary', icon: 'bi-play-circle-fill', label: 'In Progress' },
            completed: { badge: 'bg-success', icon: 'bi-check-circle-fill', label: 'Completed' },
        };
        const sc = statusConfig[route.status] || { badge: 'bg-secondary', icon: 'bi-circle', label: route.status };

        const progress = route.progress;
        const progressPct = (progress && progress.total)
            ? Math.round(progress.completed / progress.total * 100)
            : 0;

        const progressBar = progress ? `
            <div class="mt-2">
                <div class="d-flex justify-content-between small text-muted mb-1">
                    <span>${progress.completed} / ${progress.total} stops</span>
                    <span>${progressPct}%</span>
                </div>
                <div class="progress" style="height:5px;">
                    <div class="progress-bar ${route.status === 'completed' ? 'bg-success' : ''}"
                        style="width:${progressPct}%"></div>
                </div>
            </div>
        ` : '';

        const cardBorder = route.status === 'in_progress' ? 'border-primary' : '';
        const noOpWarning = !hasOperator
            ? `<span class="badge bg-warning text-dark ms-1"><i class="bi bi-exclamation-triangle-fill me-1"></i>No Operator</span>`
            : '';

        return `
            <div class="col-12 col-md-6 col-xl-4">
                <div class="card h-100 ${cardBorder}" style="cursor:pointer;"
                    data-action="open-route" data-route-id="${route.id}">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <div class="fw-semibold text-truncate me-2">${templateName}</div>
                            <span class="badge ${sc.badge} flex-shrink-0">
                                <i class="bi ${sc.icon} me-1"></i>${sc.label}
                            </span>
                        </div>
                        <div class="d-flex align-items-center gap-1 text-muted small">
                            <i class="bi bi-person-fill"></i>
                            ${hasOperator ? operatorName : '<span class="text-warning">Unassigned</span>'}
                            ${noOpWarning}
                        </div>
                        ${progressBar}
                    </div>
                </div>
            </div>
        `;
    }

    get template() {
        const inProgress = this.routes.filter(r => r.status === 'in_progress');
        const scheduled = this.routes.filter(r => r.status === 'scheduled');
        const completed = this.routes.filter(r => r.status === 'completed');
        const reqCount = this.pendingRequests.length;

        const sectionHtml = (title, routes) => {
            if (routes.length === 0) return '';
            return `
                <div class="mb-4">
                    <h6 class="text-muted mb-3">${title} (${routes.length})</h6>
                    <div class="row g-3">
                        ${routes.map(r => this._routeCardHtml(r)).join('')}
                    </div>
                </div>
            `;
        };

        const requestsSectionHtml = reqCount > 0 ? `
            <div class="mb-4">
                <h6 class="text-muted mb-2 d-flex align-items-center gap-2">
                    <span>Field Requests</span>
                    <span class="badge bg-warning text-dark">${reqCount}</span>
                </h6>
                <div class="card border-warning">
                    <div class="card-header py-2 bg-warning bg-opacity-10 border-bottom border-warning small text-warning-emphasis fw-semibold">
                        <i class="bi bi-hand-index-thumb me-1"></i>
                        Operators have requested ${reqCount} new stop${reqCount !== 1 ? 's' : ''} — review to approve or reject.
                    </div>
                    <div class="card-body p-0">
                        ${this.pendingRequests.map(r => this._requestCardHtml(r)).join('')}
                    </div>
                </div>
            </div>
        ` : '';

        return `
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <h5 class="mb-0">
                    <i class="bi bi-broadcast text-danger me-2"></i>Live View
                    <span class="ms-2 text-muted small fw-normal">Today</span>
                    ${reqCount > 0 ? `<span class="badge bg-warning text-dark ms-2">${reqCount} request${reqCount !== 1 ? 's' : ''}</span>` : ''}
                </h5>
                <div class="d-flex gap-2">
                    <button type="button" class="btn btn-outline-secondary btn-sm" data-action="open-lock-feed">
                        <i class="bi bi-shield-lock me-1"></i>Lock Feed
                    </button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" data-action="view-map">
                        <i class="bi bi-map me-1"></i>Map
                    </button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" data-action="refresh">
                        <i class="bi bi-arrow-clockwise"></i>
                    </button>
                    <button type="button" class="btn btn-primary btn-sm" data-action="schedule-route">
                        <i class="bi bi-plus-lg me-1"></i>Schedule
                    </button>
                </div>
            </div>

            ${requestsSectionHtml}

            ${inProgress.length === 0 && scheduled.length === 0 ? `
                <div class="text-center py-5 text-muted">
                    <i class="bi bi-calendar-x fs-2 d-block mb-2"></i>
                    No routes active or scheduled for today.
                    <div class="mt-2">
                        <button type="button" class="btn btn-primary btn-sm" data-action="schedule-route">
                            <i class="bi bi-plus-lg me-1"></i>Schedule a Route
                        </button>
                    </div>
                </div>
            ` : ''}
            ${sectionHtml('In Progress', inProgress)}
            ${sectionHtml('Scheduled Today', scheduled)}
            ${sectionHtml('Completed Today', completed)}
        `;
    }

    set template(v) { this._t = v; }
}
